import { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    UploadCloud, FileText, CheckCircle, XCircle, Download, Clock, Loader2, AlertCircle,
} from 'lucide-react';
import Papa from 'papaparse';
import { uploadBatchCSV, getBatchStatus, ClassificationResult } from '../services/api';
import ConfidenceGauge from '../components/ConfidenceGauge';
import toast from 'react-hot-toast';

type JobStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';

interface BatchResult extends ClassificationResult {
    sku: string;
    product_description: string;
    error?: string;
}

export default function BatchPage() {
    const [jobStatus, setJobStatus] = useState<JobStatus>('idle');
    const [progress, setProgress] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [results, setResults] = useState<BatchResult[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;
        if (!file.name.endsWith('.csv')) {
            toast.error('请上传 CSV 格式文件（.csv）');
            return;
        }
        setSelectedFile(file);
        setJobStatus('idle');
        setResults([]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        maxFiles: 1,
        maxSize: 10 * 1024 * 1024,
    });

    const startPolling = (batchId: string) => {
        pollRef.current = setInterval(async () => {
            try {
                const data = await getBatchStatus(batchId);
                setProgress(data.job.progress);
                setTotalItems(data.job.total_items);

                if (data.job.status === 'completed') {
                    clearInterval(pollRef.current!);
                    setResults(data.classifications as BatchResult[]);
                    setJobStatus('completed');
                    toast.success(`批量完成！已分类 ${data.classifications.length} 个产品`);
                } else if (data.job.status === 'failed') {
                    clearInterval(pollRef.current!);
                    setJobStatus('failed');
                    toast.error('批量处理失败：' + data.job.error_message);
                }
            } catch {
                // silently retry
            }
        }, 1500);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setJobStatus('uploading');
        try {
            const data = await uploadBatchCSV(selectedFile);
            setTotalItems(data.total_items);
            setJobStatus('processing');
            startPolling(data.batch_id);
            toast.success(`正在处理 ${data.total_items} 个产品...`);
        } catch {
            setJobStatus('failed');
            toast.error('上传失败，请检查 CSV 文件格式');
        }
    };

    /**
     * Estimate total effective CN tariff from HTS code + base duty rate.
     * Mirrors the logic in backend/src/services/taxCalculator.ts.
     * Used for export only — actual calculation should use /api/tax/calculate.
     */
    const estimateCNTotalRate = (htsCode: string, baseDutyRate: number): string => {
        const code = htsCode.replace(/\./g, '');
        const chapter = code.slice(0, 2);
        const heading4 = code.slice(0, 4);

        const exemptedPrefixes = ['8471','8473','8517','8528','8542','8543','8501','8504'];
        const isExempted = exemptedPrefixes.some((p) => heading4.startsWith(p));

        const s301_25 = new Set(['39','48','61','62','63','64','73','76','82','83','84','85','87','88','89','90','91','94','95']);
        const s301_75 = new Set(['22','36','40','44','45','46','59','66','68','69','70','71','93','96']);

        let additional = 0.20; // 20% fentanyl EO (all CN goods)
        if (!isExempted) additional += 1.25; // 125% reciprocal tariff
        if (s301_25.has(chapter)) additional += 0.25;
        else if (s301_75.has(chapter)) additional += 0.075;

        return ((baseDutyRate + additional) * 100).toFixed(1) + '%';
    };

    const downloadResults = () => {
        if (!results.length) return;
        const rows = results.map((r) => ({
            SKU: r.sku,
            产品描述: r.product_description,
            HTS编码: r.hts_code_formatted,
            置信度: r.confidence,
            基础税率: r.duty_rate > 0 ? (r.duty_rate * 100).toFixed(2) + '%' : 'N/A(查询不到基础税率)',
            对华综合税率估算: r.error ? 'N/A' : estimateCNTotalRate(r.hts_code, r.duty_rate),
            错误: r.error || '',
        }));
        // Add disclaimer header row
        const disclaimer = [{ SKU: '// 说明: 基础税率=HTSUS基础税率(0%表示免税或查询不到); 对华综合税率估算=含Section301+2025行政令,仅供参考,请以报关行核实为准', 产品描述:'', HTS编码:'', 置信度:'', 基础税率:'', 对华综合税率估算:'', 错误:'' }];
        const csv = Papa.unparse([...disclaimer, ...rows]);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hts_分类结果_${Date.now()}.csv`;
        a.click();
    };

    const downloadTemplate = () => {
        const csv = 'sku,description,unit_cost\nSKU001,Wireless Bluetooth headphones over-ear,29.99\nSKU002,Men cotton t-shirt crew neck,8.50';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'hscodify_模板.csv';
        a.click();
    };

    const confidenceBadge = (c: number) =>
        c >= 80 ? 'badge-green' : c >= 50 ? 'badge-yellow' : 'badge-red';

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                    <UploadCloud size={20} className="text-brand-400" />
                    <h1 className="text-xl font-bold text-white">批量 CSV 上传</h1>
                    <span className="badge badge-blue ml-1">最多 1,000 个 SKU</span>
                </div>
                <p className="text-slate-400 text-sm">上传 CSV 文件，AI 批量分类 HTS 编码。支持 SKU、description 两列格式</p>
            </div>

            {/* Drop zone */}
            {(jobStatus === 'idle' || jobStatus === 'uploading') && (
                <div className="card mb-4">
                    <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-200 ${isDragActive
                                ? 'border-brand-500 bg-brand-500/10'
                                : selectedFile
                                    ? 'border-emerald-500/40 bg-emerald-500/5'
                                    : 'border-white/10 hover:border-brand-500/40 hover:bg-brand-500/5'
                            }`}
                    >
                        <input {...getInputProps()} />
                        {selectedFile ? (
                            <div className="flex flex-col items-center gap-3">
                                <CheckCircle size={36} className="text-emerald-400" />
                                <div>
                                    <p className="text-white font-medium">{selectedFile.name}</p>
                                    <p className="text-slate-500 text-sm">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                </div>
                                <p className="text-xs text-slate-600">点击或拖拽替换</p>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-3">
                                <UploadCloud size={36} className="text-slate-600" />
                                <div>
                                    <p className="text-white font-medium">
                                        {isDragActive ? '释放以上传 CSV 文件' : '拖拽 CSV 文件至此'}
                                    </p>
                                    <p className="text-slate-500 text-sm mt-1">或点击选择文件 · 最多 1,000 行 · 仅限 .csv</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between mt-4">
                        <button onClick={downloadTemplate} className="btn-secondary text-sm gap-1.5">
                            <FileText size={14} />
                            下载模板
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={!selectedFile || jobStatus === 'uploading'}
                            className="btn-primary"
                        >
                            {jobStatus === 'uploading' ? (
                                <><Loader2 size={16} className="animate-spin" /> 上传中...</>
                            ) : (
                                <><UploadCloud size={16} /> 开始分类</>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Progress */}
            {jobStatus === 'processing' && (
                <div className="card mb-4 animate-fade-in">
                    <div className="flex items-center gap-3 mb-4">
                        <Clock size={18} className="text-brand-400 animate-pulse-slow" />
                        <div>
                            <p className="text-white font-medium">正在处理 {totalItems} 个产品...</p>
                            <p className="text-slate-500 text-sm">AI 正在逐一分类，预计需要几分钟</p>
                        </div>
                    </div>
                    <div className="h-2 bg-surface-2 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-brand-600 to-violet-500 rounded-full transition-all duration-500"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-slate-500">
                        <span>{Math.round(progress * totalItems / 100)} / {totalItems} 已完成</span>
                        <span>{progress}%</span>
                    </div>
                </div>
            )}

            {/* Failed */}
            {jobStatus === 'failed' && (
                <div className="card mb-4 flex items-center gap-3 border-red-500/20 bg-red-500/5 animate-fade-in">
                    <XCircle size={20} className="text-red-400 flex-shrink-0" />
                    <div>
                        <p className="text-white font-medium">批量处理失败</p>
                        <p className="text-slate-500 text-sm">请检查 CSV 文件格式后重试</p>
                    </div>
                    <button onClick={() => setJobStatus('idle')} className="btn-secondary ml-auto text-sm">
                        重试
                    </button>
                </div>
            )}

            {/* Results */}
            {jobStatus === 'completed' && results.length > 0 && (
                <div className="space-y-4 animate-slide-up">
                    {/* Stats row */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                            { label: '总分类数', value: results.length, color: 'text-white' },
                            {
                                label: '高置信度 (≥80%)',
                                value: results.filter((r) => r.confidence >= 80).length,
                                color: 'text-emerald-400',
                            },
                            {
                                label: '需复核 (<50%)',
                                value: results.filter((r) => r.confidence < 50).length,
                                color: 'text-amber-400',
                            },
                        ].map((s) => (
                            <div key={s.label} className="card text-center">
                                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                                <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="card p-0 overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
                            <p className="text-sm font-semibold text-white">分类结果 — {results.length} 个产品</p>
                            <button onClick={downloadResults} className="btn-secondary text-sm gap-1.5">
                                <Download size={14} />
                                导出 CSV
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/[0.06] text-left">
                                        <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">产品描述</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">HTS 编码</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">置信度</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">基础税率</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((row, i) => (
                                        <tr key={i} className="border-b border-white/[0.04] table-row-hover">
                                            <td className="px-4 py-3 font-mono text-brand-300 text-xs">{row.sku}</td>
                                            <td className="px-4 py-3 text-slate-300 max-w-xs">
                                                <p className="line-clamp-1 text-xs">{row.product_description}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                {row.error ? (
                                                    <span className="flex items-center gap-1 text-red-400 text-xs">
                                                        <AlertCircle size={12} /> 错误
                                                    </span>
                                                ) : (
                                                    <span className="hts-code">{row.hts_code_formatted}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {row.confidence > 0 && (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-16 h-1.5 bg-surface-2 rounded-full">
                                                            <div
                                                                className={`h-full rounded-full ${row.confidence >= 80 ? 'bg-emerald-500' : row.confidence >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                                                    }`}
                                                                style={{ width: `${row.confidence}%` }}
                                                            />
                                                        </div>
                                                        <span className={`text-xs ${confidenceBadge(row.confidence)}`}>{row.confidence}%</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-amber-400 text-xs font-mono">
                                                {row.duty_rate != null ? `${(row.duty_rate * 100).toFixed(1)}%` : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <button onClick={() => { setJobStatus('idle'); setSelectedFile(null); setResults([]); }} className="btn-secondary text-sm">
                        ← 重新上传
                    </button>
                </div>
            )}
        </div>
    );
}
